import type {
  CreateSpotMutation,
  CommissionInput,
  VoyageCharterExtraFreightInput,
  VoyageCharterInput,
  VoyageCharterOrderFreight,
  VoyageCharterPatch,
  PortDatumInput,
  VoyageCharterExtraCostInput,
  MakeOptional,
} from '../../graphql';
import {
  useCreateCommissionMutation,
  useCreateExtraFreightMutation,
  useCreateOrderSpotRelationMutation,
  useCreateSpotMutation,
  useCreateVoyageCharterOrderFreightMutation,
  useDeleteExtraFreightMutation,
  useUpdateExtraFreightMutation,
  useUpdateSpotMutation,
  useUpdateCommissionMutation,
  useDeleteCommissionMutation,
  useUpdateVoyageCharterOrderFreightMutation,
  useCreateSpotPortDataMutation,
  useCreatePortDataMutation,
  useCreateExtraCostMutation,
  useDeleteExtraCostMutation,
  useUpdateExtraCostMutation,
} from '../../graphql';
import omit from 'lodash/omit';
import type { Forbidden, WithId, WithNullableId } from '../../lib/util';
import { assertDefined } from '../../lib/util';
import type { FetchResult } from '@apollo/client';
import { gql } from '@apollo/client';
import type { SpotOrderType } from './SpotForm';
import type { VoyageCharterSchema } from './schema';
import { isClientId } from '../core/ClientId';
import { useCurrentUser } from '../../state/zustand';
import { useUpdatePortData } from '../contracts/portData';

type OrderRelationAndFreightInput = {
  orderId: number;
  relationId: number | null;
  freightId: number | null;
  freightAmount: VoyageCharterOrderFreight['freightAmount'];
  freightUnit: VoyageCharterOrderFreight['freightUnit'];
};

export type CreateSpotWithRelationsMutation = (
  spotContract: Omit<Forbidden<VoyageCharterInput, 'id'>, 'organisationId'>,
  orderSpotRelationsAndFreights: Array<OrderRelationAndFreightInput>,
  portData: Array<Forbidden<PortDatumInput, 'id'>>,
  commissions: Array<WithNullableId<CommissionInput>>,
  extraFreights: Array<
    WithNullableId<
      MakeOptional<VoyageCharterExtraFreightInput, 'voyageCharterId'>
    >
  >,
) => Promise<[FetchResult<CreateSpotMutation>]>;

export function useCreateSpotWithRelationsMutation(): [
  CreateSpotWithRelationsMutation,
] {
  const [createSpot] = useCreateSpotMutation();
  const [createOrderSpotRelation] = useCreateOrderSpotRelationMutation({
    update: (cache, { data }) => {
      const newRel =
        data?.createOrderVoyageCharterRelation?.orderVoyageCharterRelation;

      if (!newRel) return;

      const newRelRef = cache.writeFragment({
        data: newRel,
        fragment: gql`
          fragment NewRelation on OrderVoyageCharterRelation {
            nodeId
            id
            orderId
            voyageCharterId
            outgoing
            coaRouteId
          }
        `,
      });

      cache.modify({
        id: cache.identify({
          __typename: 'Order',
          id: newRel.orderId,
        }),
        fields: {
          orderVoyageCharterRelationsByOrderId: (existingRefs) => ({
            nodes: [...existingRefs.nodes, newRelRef],
          }),
        },
      });
    },
  });
  const [createVoyageCharterOrderFreight] =
    useCreateVoyageCharterOrderFreightMutation({
      update: (cache, { data }) => {
        const newFreight =
          data?.createVoyageCharterOrderFreight?.voyageCharterOrderFreight;

        if (!newFreight) return;

        const newFreightRef = cache.writeFragment({
          data: newFreight,
          fragment: gql`
            fragment NewFreight on VoyageCharterOrderFreight {
              id
            }
          `,
        });

        cache.modify({
          id: cache.identify({
            __typename: 'OrderVoyageCharterRelation',
            id: newFreight.orderVoyageCharterRelationId,
          }),
          fields: {
            voyageCharterOrderFreightsByOrderVoyageCharterRelationId: (
              existingRefs,
            ) => ({
              nodes: [...existingRefs.nodes, newFreightRef],
            }),
          },
        });
      },
    });

  const [createPortData] = useCreatePortDataMutation();
  const [createPortDataRelation] = useCreateSpotPortDataMutation({
    update: (cache, { data }) => {
      const newRel = data?.createVoyageCharterPortDatum?.voyageCharterPortDatum;

      if (!newRel) return;

      const newRelRef = cache.writeFragment({
        data: newRel,
        fragment: gql`
          fragment NewRelation on VoyageCharterPortDatum {
            id
            voyageCharterId
            portDataId
          }
        `,
      });

      cache.modify({
        id: cache.identify({
          __typename: 'VoyageCharter',
          id: newRel.voyageCharterId,
        }),
        fields: {
          voyageCharterPortDataByVoyageCharterId: (existingRefs) => ({
            nodes: [...existingRefs.nodes, newRelRef],
          }),
        },
      });
    },
  });

  const { updateCommissions } = useCommissions();
  const { updateExtraFreights } = useExtraFreights();
  const currentUser = useCurrentUser();
  const organisationId = currentUser?.organizationId ?? null;

  const mut: CreateSpotWithRelationsMutation = async (
    spotContract,
    orderSpotRelationsAndFreights,
    portData,
    commissions,
    extraFreights,
  ) => {
    if (typeof organisationId !== 'number') {
      throw new Error('Unable to get logged in users organisationId');
    }

    const createSpotRes = await createSpot({
      variables: {
        input: {
          voyageCharter: {
            organisationId,
            ...omit(spotContract, 'id'),
          },
        },
      },
    });

    const createdSpotId =
      createSpotRes.data?.createVoyageCharter?.voyageCharter?.id;

    assertDefined(createdSpotId, 'created voyageCharter id');

    const createOrderSpotRelationResponses = await Promise.all(
      orderSpotRelationsAndFreights.map((or) =>
        createOrderSpotRelation({
          refetchQueries: ['PlanningPage'],
          variables: {
            input: {
              orderVoyageCharterRelation: {
                voyageCharterId: createdSpotId,
                orderId: or.orderId,
                outgoing: true,
              },
            },
          },
        }),
      ),
    );

    const createdOrderSpotRelationIds = createOrderSpotRelationResponses.map(
      (res) => {
        assertDefined(
          res.data?.createOrderVoyageCharterRelation
            ?.orderVoyageCharterRelation,
          'created order voyage charter relation',
        );

        return res.data.createOrderVoyageCharterRelation
          .orderVoyageCharterRelation.id;
      },
    );

    await Promise.all(
      orderSpotRelationsAndFreights.map((or, index) =>
        createVoyageCharterOrderFreight({
          refetchQueries: ['VoyageIncome'],
          variables: {
            input: {
              voyageCharterOrderFreight: {
                orderVoyageCharterRelationId:
                  createdOrderSpotRelationIds[index],
                freightAmount: or.freightAmount,
                freightUnit: or.freightUnit,
              },
            },
          },
        }),
      ),
    );

    const createPortDataResponses = await Promise.all(
      portData.map((portDatum) =>
        createPortData({
          variables: {
            input: {
              portDatum,
            },
          },
        }),
      ),
    );

    const createdPortDataIds = createPortDataResponses.map((res): number => {
      const createdId = res.data?.createPortDatum?.portDatum?.id;

      assertDefined(createdId, 'created portDatum id');

      return createdId;
    });

    createdPortDataIds.map((portDataId) =>
      createPortDataRelation({
        variables: {
          input: {
            voyageCharterPortDatum: {
              portDataId,
              voyageCharterId: createdSpotId,
            },
          },
        },
      }),
    );

    await updateCommissions(createdSpotId, commissions, []);
    await updateExtraFreights(createdSpotId, extraFreights, []);

    return [createSpotRes];
  };

  return [mut];
}

export type UpdateSpotWithRelationsMutation = (
  spotContractPatch: WithId<VoyageCharterPatch>,
  oldRelationsAndFreights: Array<SpotOrderType>,
  newRelationsAndFreights: Array<SpotOrderType>,
  newPortData: Array<WithNullableId<PortDatumInput>>,
  oldPortData: Array<WithId<PortDatumInput>>,
  newCommissions: Array<WithNullableId<CommissionInput>>,
  oldCommissions: Array<WithId<CommissionInput>>,
  newExtraFreights: Array<
    WithNullableId<
      MakeOptional<VoyageCharterExtraFreightInput, 'voyageCharterId'>
    >
  >,
  oldExtraFreights: Array<
    WithId<MakeOptional<VoyageCharterExtraFreightInput, 'voyageCharterId'>>
  >,
  outgoing: boolean,
) => Promise<unknown>;

export function useUpdateSpotWithRelationsMutation(): [
  UpdateSpotWithRelationsMutation,
] {
  const [updateSpot] = useUpdateSpotMutation();
  const [createOrderSpotRelation] = useCreateOrderSpotRelationMutation();
  const [createVoyageCharterOrderFreight] =
    useCreateVoyageCharterOrderFreightMutation();
  const [createPortDataRelation] = useCreateSpotPortDataMutation();
  const [updatePortData] = useUpdatePortData();
  const [updateVoyageCharterOrderFreight] =
    useUpdateVoyageCharterOrderFreightMutation();
  const { updateCommissions } = useCommissions();
  const { updateExtraFreights } = useExtraFreights();

  const mut: UpdateSpotWithRelationsMutation = async (
    spotContractPatch,
    oldRelationsAndFreights,
    newRelationsAndFreights,
    newPortData,
    oldPortData,
    newCommissions,
    oldCommissions,
    newExtraFreights,
    oldExtraFreights,
    outgoing,
  ) => {
    await updateSpot({
      variables: {
        input: {
          id: spotContractPatch.id,
          voyageCharterPatch: {
            ...omit(spotContractPatch, 'id'),
          },
        },
      },
    });
    const oldOrderIds = oldRelationsAndFreights.map((or) => or.orderId);
    const newOrderIds = newRelationsAndFreights.map((or) => or.orderId);

    const createdRelationOrderIds = newOrderIds.filter(
      (orderId) => !oldOrderIds.includes(orderId),
    );

    const createOrderRelationResponses = await Promise.all(
      createdRelationOrderIds.map((orderId) =>
        createOrderSpotRelation({
          refetchQueries: ['PlanningPage'],
          variables: {
            input: {
              orderVoyageCharterRelation: {
                voyageCharterId: spotContractPatch.id,
                orderId,
                outgoing,
              },
            },
          },
        }),
      ),
    );

    const createdRelationIdByOrderId = createOrderRelationResponses.reduce<
      Map<number, number>
    >((map, res) => {
      const rel =
        res.data?.createOrderVoyageCharterRelation?.orderVoyageCharterRelation;

      assertDefined(rel, 'created order voyage charter relation');

      return map.set(rel.orderId, rel.id);
    }, new Map());

    const createdFreights = newRelationsAndFreights.filter(
      (or) => !or.freightId,
    );

    await Promise.all(
      createdFreights.map((or) => {
        const relationId =
          or.relationId || createdRelationIdByOrderId.get(or.orderId);

        assertDefined(relationId, 'relation id for new freight');

        return createVoyageCharterOrderFreight({
          refetchQueries: ['VoyageIncome'],
          variables: {
            input: {
              voyageCharterOrderFreight: {
                orderVoyageCharterRelationId: relationId,
                freightAmount: or.freightAmount,
                freightUnit: or.freightUnit,
              },
            },
          },
        });
      }),
    );

    const updatedFreights = newRelationsAndFreights.filter((or) => {
      const oldFreight = oldRelationsAndFreights.find(
        (freight) =>
          freight.freightId !== null && freight.freightId === or.freightId,
      );

      return (
        oldFreight &&
        (or.freightAmount !== oldFreight.freightAmount ||
          or.freightUnit !== oldFreight.freightUnit)
      );
    });

    await Promise.all(
      updatedFreights.map((freight) => {
        assertDefined(freight.freightId, 'updated freight id');

        return updateVoyageCharterOrderFreight({
          refetchQueries: ['VoyageIncome'],
          variables: {
            input: {
              id: freight.freightId,
              voyageCharterOrderFreightPatch: {
                freightAmount: freight.freightAmount,
                freightUnit: freight.freightUnit,
              },
            },
          },
        });
      }),
    );

    await updateCommissions(
      spotContractPatch.id,
      newCommissions,
      oldCommissions,
    );
    await updateExtraFreights(
      spotContractPatch.id,
      newExtraFreights,
      oldExtraFreights,
    );

    const { createPortDataResponses } = await updatePortData(
      newPortData,
      oldPortData,
    );

    const createdPortDataIds = createPortDataResponses.map((res): number => {
      const createdId = res.data?.createPortDatum?.portDatum?.id;

      assertDefined(createdId, 'created portDatum id');

      return createdId;
    });

    await Promise.all(
      createdPortDataIds.map((portDataId) =>
        createPortDataRelation({
          variables: {
            input: {
              voyageCharterPortDatum: {
                voyageCharterId: spotContractPatch.id,
                portDataId,
              },
            },
          },
        }),
      ),
    );
  };

  return [mut];
}

export function useCommissions() {
  const [createCommission] = useCreateCommissionMutation();
  const [deleteCommission] = useDeleteCommissionMutation();
  const [updateCommission] = useUpdateCommissionMutation();

  async function updateCommissions(
    voyageCharterId: number,
    newCommissions: Array<WithNullableId<CommissionInput>>,
    oldCommissions: Array<WithId<CommissionInput>>,
  ) {
    const createdCommissions = newCommissions.filter((cm) => cm.id === null);
    const deletedCommissions = oldCommissions.filter(
      (oldCm) => !newCommissions.find((newCm) => oldCm.id === newCm.id),
    );

    const updatedCommissions = newCommissions.filter(
      (newCm): newCm is WithId<CommissionInput> =>
        !!oldCommissions.find(
          (oldCm) =>
            newCm.id === oldCm.id &&
            (newCm.type !== oldCm.type ||
              newCm.value !== oldCm.value ||
              newCm.organizationId !== oldCm.organizationId ||
              newCm.demurrageApplied !== oldCm.demurrageApplied ||
              newCm.bafApplied !== oldCm.bafApplied ||
              newCm.extraFreightApplied !== oldCm.extraFreightApplied ||
              newCm.assortmentId !== oldCm.assortmentId ||
              newCm.loadPortId !== oldCm.loadPortId ||
              newCm.dischargePortId !== oldCm.dischargePortId),
        ),
    );

    await Promise.all(
      createdCommissions.map(
        ({
          type,
          value,
          bafApplied,
          demurrageApplied,
          extraFreightApplied,
          organizationId,
          assortmentId,
          loadPortId,
          dischargePortId,
        }) =>
          createCommission({
            variables: {
              input: {
                commission: {
                  type,
                  value,
                  bafApplied,
                  demurrageApplied,
                  extraFreightApplied,
                  organizationId: organizationId ? organizationId : null,
                  voyageCharterId,
                  assortmentId: assortmentId ? assortmentId : null,
                  loadPortId,
                  dischargePortId,
                },
              },
            },
          }),
      ),
    );

    await Promise.all(
      deletedCommissions.map((cm) => {
        deleteCommission({
          variables: {
            input: {
              id: cm.id,
            },
          },
        });
      }),
    );

    await Promise.all(
      updatedCommissions.map(
        ({
          id,
          type,
          value,
          bafApplied,
          demurrageApplied,
          extraFreightApplied,
          organizationId,
          assortmentId,
          loadPortId,
          dischargePortId,
        }) =>
          updateCommission({
            variables: {
              input: {
                id,
                commissionPatch: {
                  type,
                  value,
                  organizationId: organizationId ? organizationId : null,
                  bafApplied,
                  demurrageApplied,
                  extraFreightApplied,
                  assortmentId: assortmentId ? assortmentId : null,
                  loadPortId,
                  dischargePortId,
                },
              },
            },
          }),
      ),
    );
  }
  return {
    updateCommissions,
  };
}
export function useExtraFreights() {
  const [createExtraFreight] = useCreateExtraFreightMutation();
  const [deleteExtraFreight] = useDeleteExtraFreightMutation();
  const [updateExtraFreight] = useUpdateExtraFreightMutation();

  async function updateExtraFreights(
    voyageCharterId: number,
    newExtraFreights: Array<
      WithNullableId<
        MakeOptional<VoyageCharterExtraFreightInput, 'voyageCharterId'>
      >
    >,
    oldExtraFreights: Array<
      WithId<MakeOptional<VoyageCharterExtraFreightInput, 'voyageCharterId'>>
    >,
  ) {
    const createdExtraFreights = newExtraFreights.filter(
      (ef) => ef.id === null,
    );

    const deletedExtraFreights = oldExtraFreights.filter(
      (oldEf) => !newExtraFreights.find((newEf) => oldEf.id === newEf.id),
    );

    const updatedExtraFreights = newExtraFreights.filter(
      (newEf): newEf is WithId<VoyageCharterExtraFreightInput> =>
        !!oldExtraFreights.find(
          (oldEf) =>
            newEf.id === oldEf.id &&
            (newEf.name !== oldEf.name ||
              newEf.amount !== oldEf.amount ||
              newEf.type !== oldEf.type ||
              newEf.description !== oldEf.description),
        ),
    );

    await Promise.all(
      createdExtraFreights.map(({ amount, name, type, description }) =>
        createExtraFreight({
          variables: {
            input: {
              voyageCharterExtraFreight: {
                amount,
                description,
                name,
                type,
                voyageCharterId,
              },
            },
          },
        }),
      ),
    );

    await Promise.all(
      deletedExtraFreights.map((ef) =>
        deleteExtraFreight({
          variables: {
            input: {
              id: ef.id,
            },
          },
        }),
      ),
    );

    await Promise.all(
      updatedExtraFreights.map(({ id, amount, name, type, description }) =>
        updateExtraFreight({
          variables: {
            input: {
              id,
              voyageCharterExtraFreightPatch: {
                amount,
                description,
                name,
                type,
              },
            },
          },
        }),
      ),
    );
  }
  return {
    updateExtraFreights,
  };
}

export function useExtraCosts() {
  const [createExtraCost] = useCreateExtraCostMutation();
  const [deleteExtraCost] = useDeleteExtraCostMutation();
  const [updateExtraCost] = useUpdateExtraCostMutation();

  async function updateExtraCosts(
    voyageCharterId: number,
    newExtraCosts: Array<WithNullableId<VoyageCharterExtraCostInput>>,
    oldExtraCosts: Array<WithId<VoyageCharterExtraCostInput>>,
  ) {
    const createdExtraFreights = newExtraCosts.filter((ef) => ef.id === null);

    const deletedExtraFreights = oldExtraCosts.filter(
      (oldEc) => !newExtraCosts.find((newEc) => oldEc.id === newEc.id),
    );

    const updatedExtraFreights = newExtraCosts.filter(
      (newEc): newEc is WithId<VoyageCharterExtraCostInput> =>
        !!oldExtraCosts.find(
          (oldEc) =>
            newEc.id === oldEc.id &&
            (newEc.name !== oldEc.name ||
              newEc.amount !== oldEc.amount ||
              newEc.type !== oldEc.type ||
              newEc.description !== oldEc.description),
        ),
    );

    await Promise.all(
      createdExtraFreights.map(({ amount, name, type, description }) =>
        createExtraCost({
          variables: {
            input: {
              voyageCharterExtraCost: {
                amount,
                description,
                name,
                type,
                voyageCharterId,
              },
            },
          },
        }),
      ),
    );

    await Promise.all(
      deletedExtraFreights.map((ef) =>
        deleteExtraCost({
          variables: {
            input: {
              id: ef.id,
            },
          },
        }),
      ),
    );

    await Promise.all(
      updatedExtraFreights.map(({ id, amount, name, type, description }) =>
        updateExtraCost({
          variables: {
            input: {
              id,
              voyageCharterExtraCostPatch: {
                amount,
                description,
                name,
                type,
              },
            },
          },
        }),
      ),
    );
  }
  return {
    updateExtraCosts,
  };
}

export function isPersistedSpotOrder(
  o: VoyageCharterSchema['relatedOrders'][number],
): o is typeof o & { orderId: number } {
  return !isClientId(o.orderId);
}
